import { computed, getCurrentInstance, ref, watch } from "vue";
import { useAuthSessionStore } from "@/stores/authSession";
import { isScribeAccessArchivedError, scribeAccessArchivedSignal } from "@/helpers/scribeAccess";
import { getPacksWithFilters } from "@/components/scribe/api";

// Verdicts per organization for this page session. Only a definite answer is kept: an archived 403
// (true) or a successful request (false). A network, auth or server failure leaves no verdict, so the
// next surface that asks probes again rather than being stuck with "not archived" until a reload.
const verdictsByOrganization = ref(new Map<string, boolean>());
const probesInFlight = new Map<string, Promise<boolean>>();

/**
 * How long a caller that awaits `confirmArchived()` waits before treating the answer as "not archived
 * (yet)", so an unbounded Scribe request or token refresh can never hold its caller forever. A probe
 * that answers later still records its verdict, and the notice appears reactively. Load-failure paths
 * should not await it at all (LSP-21848): show the failure, fire the probe, and let `isArchived` swap
 * in the notice.
 */
export const ACCESS_PROBE_TIMEOUT_MS = 5_000;

function probe(organizationId: string, currentOrganizationId: () => string | null): Promise<boolean> {
	const inFlight = probesInFlight.get(organizationId);
	if (inFlight) return inFlight;
	// The request's token is fetched after this point, so if the user switches organization while it is
	// in flight the answer may describe either organization. It is kept only when the organization is
	// still the one that asked; otherwise it is discarded and the next ask probes again.
	const answeredForAskingOrganization = () => {
		if (currentOrganizationId() === organizationId) return true;
		console.debug("[useScribeAccess] discarding an access answer that landed after an organization switch", {
			organizationId,
			currentOrganizationId: currentOrganizationId()
		});
		return false;
	};
	const request = getPacksWithFilters({ limit: 1 })
		.then(
			() => {
				if (!answeredForAskingOrganization()) return false;
				verdictsByOrganization.value = new Map(verdictsByOrganization.value).set(organizationId, false);
				return false;
			},
			(error: unknown) => {
				if (!isScribeAccessArchivedError(error)) {
					console.debug("[useScribeAccess] access probe failed without an answer; will retry on next ask", {
						organizationId,
						error
					});
					return false;
				}
				if (!answeredForAskingOrganization()) return false;
				console.warn("[useScribeAccess] Scribe reports this user's access is archived for the organization", { organizationId });
				verdictsByOrganization.value = new Map(verdictsByOrganization.value).set(organizationId, true);
				return true;
			}
		)
		.finally(() => probesInFlight.delete(organizationId));
	probesInFlight.set(organizationId, request);
	return request;
}

function withinProbeBound(answer: Promise<boolean>, organizationId: string): Promise<boolean> {
	let timer: ReturnType<typeof setTimeout> | undefined;
	const timeout = new Promise<boolean>((resolve) => {
		timer = setTimeout(() => {
			console.warn("[useScribeAccess] access probe did not answer in time; treating access as not archived for now", {
				organizationId,
				timeoutMs: ACCESS_PROBE_TIMEOUT_MS
			});
			resolve(false);
		}, ACCESS_PROBE_TIMEOUT_MS);
	});
	return Promise.race([answer, timeout]).finally(() => clearTimeout(timer));
}

/**
 * LSP-17976: whether Scribe has archived this user for the current organization.
 *
 * Surfaces fed only through contact-api (the project Reports tab, the Reports page) never see Scribe's
 * archived 403 — contact-api turns it into an empty list or a 500 — so they call `confirmArchived()`
 * when they render an empty or failed state, without awaiting it (LSP-21848). Surfaces that call Scribe
 * directly (the Sparks lists, the conversation reports list, the Lucky pack popup) get there through the
 * funnels' signal: any archived 403 while one is mounted triggers the same confirmation. Users who can see their Sparks never pay for
 * the extra request.
 */
export function useScribeAccess() {
	const session = useAuthSessionStore();
	const isArchived = computed(() => {
		const organizationId = session.organizationId;
		return !!organizationId && verdictsByOrganization.value.get(organizationId) === true;
	});

	async function confirmArchived(): Promise<boolean> {
		const organizationId = session.organizationId;
		if (!organizationId) return false;
		const verdict = verdictsByOrganization.value.get(organizationId);
		if (verdict !== undefined) return verdict;
		return withinProbeBound(
			probe(organizationId, () => session.organizationId),
			organizationId
		);
	}

	if (getCurrentInstance()) {
		watch(scribeAccessArchivedSignal, () => {
			const organizationId = session.organizationId;
			if (!organizationId) return;
			// Access can be archived mid-session; an earlier "not archived" must not hide that.
			if (verdictsByOrganization.value.get(organizationId) === false) {
				const verdicts = new Map(verdictsByOrganization.value);
				verdicts.delete(organizationId);
				verdictsByOrganization.value = verdicts;
			}
			void confirmArchived();
		});
	}

	return { isArchived, confirmArchived };
}

/** Test-only reset of the module state. */
export function resetScribeAccessForTests(): void {
	verdictsByOrganization.value = new Map();
	probesInFlight.clear();
}
